import { useEffect, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { ModalDelete } from '../CatalogItemModal';
import { modalBackdropCls, modalCardCls, overlayCls } from '../overlay';

// Category ontology controls for the Parts database: the "+ Add category" button and
// the create/edit category modal it shares with the pencil on each folder. Extracted to
// keep NoticeCatalog under the file-size limit.

// Ontology management control: a "+ Add category" button that opens the category
// modal to create one with an English name (the identifier) and a French name.
export function AddCategory({
  existing,
  parents,
  onAdd,
  t,
}: {
  existing: string[];
  // The categories a new one may be filed inside: those not already inside another.
  parents: string[];
  onAdd: (name: string, nameFr: string, definition: string, parent: string) => Promise<void>;
  t: (k: string) => string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:border-blue-400 hover:text-blue-600"
      >
        <Plus className="h-4 w-4" />
        {t('notice.catalog.addCategory')}
      </button>
      {open && (
        <CategoryFormModal
          title={t('notice.catalog.addCategory')}
          taken={existing}
          parents={parents}
          onSave={onAdd}
          onClose={() => setOpen(false)}
          t={t}
        />
      )}
    </>
  );
}

// Modal to create a category, or to edit an existing one's English (identifier) and
// French names and its definition — renaming the English name cascades to items
// server-side. `taken` are the names refused as duplicates (case-insensitive); on edit
// the category's own name is left out of it, so re-saving it unchanged still works.
//
// The definition lives here rather than under the folder header, where it used to sit
// open on the page: a paragraph of English prose per folder, in a French table, between
// every category and its parts. It is not documentation — the classifiers are its only
// readers, and they are told to sort each part "using their definitions" — so it stays
// editable, one click away, beside the names it belongs to.
export function CategoryFormModal({
  title,
  nameEn = '',
  nameFr = '',
  definition = '',
  parent = '',
  parents = [],
  taken = [],
  onSave,
  onDelete,
  deleteConfirm,
  onClose,
  t,
}: {
  title: string;
  nameEn?: string;
  nameFr?: string;
  definition?: string;
  // The super-category this one sits inside, '' for a top level.
  parent?: string;
  // What may be chosen as one: two levels, so never a category that has a parent itself.
  parents?: string[];
  taken?: string[];
  onSave: (
    nameEn: string, nameFr: string, definition: string, parent: string,
  ) => Promise<void>;
  // Absent when creating: there is nothing to delete yet.
  onDelete?: () => Promise<void>;
  deleteConfirm?: string;
  onClose: () => void;
  t: (k: string) => string;
}) {
  const [en, setEn] = useState(nameEn);
  const [fr, setFr] = useState(nameFr);
  const [def, setDef] = useState(definition);
  const [par, setPar] = useState(parent);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = en.trim();
  const duplicate = taken.some((n) => n.toLowerCase() === trimmed.toLowerCase());

  // Escape closes, as it did when this was an inline form.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = async () => {
    if (!trimmed || duplicate || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(trimmed, fr.trim(), def.trim(), par.trim());
      onClose();
    } catch {
      setError(t('notice.catalog.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`${overlayCls} ${modalBackdropCls}`}
      onClick={onClose}
    >
      <div
        className={`w-full max-w-sm p-5 ${modalCardCls}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-4 text-center text-lg font-bold text-slate-900">{title}</h2>
        <label className="mb-1 block text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t('notice.catalog.catNameEn')}
        </label>
        <input
          autoFocus
          value={en}
          onChange={(e) => setEn(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void save()}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-center text-sm text-slate-800 outline-none focus:border-blue-500"
        />
        {duplicate ? (
          <p className="mb-3 mt-1 text-center text-xs text-rose-600">
            {t('notice.catalog.duplicateName')}
          </p>
        ) : (
          <div className="mb-3" />
        )}
        <label className="mb-1 block text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t('notice.catalog.catNameFr')}
        </label>
        <input
          value={fr}
          onChange={(e) => setFr(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void save()}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-center text-sm text-slate-800 outline-none focus:border-blue-500"
        />
        {/* Which super-category holds this one. Only offered when there is one to
            choose: on a fresh ontology every category is a top level, and a dropdown
            whose only entry is "none" is a control that can only be read wrongly. */}
        {parents.length > 0 && (
          <>
            <label className="mb-1 mt-3 block text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('notice.catalog.catParent')}
            </label>
            <select
              value={par}
              onChange={(e) => setPar(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-center text-sm text-slate-800 outline-none focus:border-blue-500"
            >
              <option value="">{t('notice.catalog.catNoParent')}</option>
              {parents.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </>
        )}
        <label className="mb-1 mt-3 block text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t('notice.catalog.definition')}
        </label>
        {/* No Enter-to-save here: a definition is prose, and prose has line breaks. */}
        <textarea
          value={def}
          onChange={(e) => setDef(e.target.value)}
          rows={3}
          placeholder={t('notice.catalog.definitionHint')}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-center text-sm text-slate-800 outline-none focus:border-blue-500"
        />
        {error && <p className="mt-2 text-center text-sm text-rose-600">{error}</p>}
        {/* Cancel/Save centred, with delete on its own line under them: the two you
            might press by mistake are the two you can undo, and a row of three centres
            on the wrong one. Delete lives inside this dialog at all — rather than as a
            second icon on the folder row, where its confirmation used to unfold inside
            the very row it was about to remove. */}
        <div className="mt-5 flex justify-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 transition-colors hover:bg-slate-100"
          >
            {t('notice.catalog.cancel')}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!trimmed || duplicate || saving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('notice.catalog.save')}
          </button>
        </div>
        {onDelete && (
          <div className="mt-3 flex justify-center">
            <ModalDelete
              label={t('notice.catalog.deleteCategory')}
              confirm={deleteConfirm || t('notice.catalog.deleteCategoryConfirm')}
              onDelete={onDelete}
              t={t}
            />
          </div>
        )}
      </div>
    </div>
  );
}
